import { useCallback, useEffect, useRef, useState } from 'react';
import affiliateService from '@wlb/shared/api/affiliate/affiliateService';
import { affiliateEarningsRangeIssue } from '@wlb/shared/api/affiliate/affiliateEarnings';
import type {
  Affiliate,
  AffiliateEarningsFilter,
} from '@wlb/shared/api/affiliate/affiliateTypes';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../../components/ui/Button';
import { FormError } from '../../components/ui/FormError';
import { Modal } from '../../components/ui/Modal';
import { TextField } from '../../components/ui/TextField';
import { useAsyncResource } from '../../hooks/useAsyncResource';
import { formatCurrency } from '../../utils/format';
import '../../components/ui/ui.css';
import '../../pages/pages.css';

interface AffiliatePayoutDialogProps {
  affiliate: Affiliate;
  /** The list's period, so the dialog reports the same interval as the tiles. */
  earningsFilter: AffiliateEarningsFilter;
  onClose: () => void;
  /** Fired after a batch is accepted, so the list re-reads its totals. */
  onPaid: () => void;
}

/** Remounts the dialog between its two steps — see the confirm branch. */
const FORM_STEP_KEY = 'affiliate-payout-form';
const CONFIRM_STEP_KEY = 'affiliate-payout-confirm';

const noop = () => {};

/**
 * Payment breakdown for one affiliate and the PayPal payout that settles it.
 *
 * The figures come from `GetAffiliateReferralCodes`, the only affiliate read
 * that reports paid, unpaid and unclaimed totals — the list endpoint answers
 * earnings and redemptions only. Read fresh on every open (no cache namespace):
 * an operator about to move money must not be shown a balance from an earlier
 * visit.
 */
export function AffiliatePayoutDialog({
  affiliate,
  earningsFilter,
  onClose,
  onPaid,
}: AffiliatePayoutDialogProps) {
  const { t } = useTranslation('admin');
  const [email, setEmail] = useState(affiliate.email);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<{ email?: string; amount?: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const amountTouched = useRef(false);

  const load = useCallback(
    () =>
      affiliateService.getReferralCodes(affiliate.affiliateUserId, {
        earningsFilter,
      }),
    [affiliate.affiliateUserId, earningsFilter],
  );

  const requestKey = [
    affiliate.affiliateUserId,
    earningsFilter.rangeId,
    earningsFilter.startDate,
    earningsFilter.endDate,
  ].join('|');

  // Gated like the list behind it: the service drops an unusable range, and
  // all-time money under the "in period" labels below is exactly the mistake an
  // operator would act on here.
  const rangeIssue = affiliateEarningsRangeIssue(earningsFilter);
  const rangeUsable = rangeIssue === 'none';
  const rangeMessage = rangeUsable
    ? null
    : t(
        rangeIssue === 'inverted'
          ? 'affiliates.earnings.rangeInvalid'
          : 'affiliates.earnings.rangeIncomplete',
      );

  const { data, status, error, reload } = useAsyncResource(
    load,
    requestKey,
    t('common.errorTitle'),
    rangeUsable,
  );

  const summary = data?.earnings ?? null;
  const due = summary?.dueEarnings ?? null;

  /**
   * A payout is only offered against a balance the API reported as owed. With no
   * figure there is nothing to cap the amount against, and the dialog would be
   * asking the operator to invent one; with nothing owed there is nothing to
   * settle. Both are states to explain, not errors.
   */
  const payable = due !== null && due > 0;
  const unpayableNote =
    status !== 'ready' || payable
      ? null
      : t(
          due === null
            ? 'affiliates.payouts.dueUnavailable'
            : 'affiliates.payouts.nothingDue',
        );

  // Prefills the balance PayPal would settle, and stops doing so the moment the
  // operator types: a reload must not overwrite a figure they chose.
  useEffect(() => {
    if (amountTouched.current || due === null || due <= 0) {
      return;
    }
    setAmount(due.toFixed(2));
  }, [due]);

  const money = (value: number | null | undefined): string =>
    value === null || value === undefined
      ? t('affiliates.earnings.notAvailable')
      : formatCurrency(value);

  const tiles: readonly { key: string; label: string; value: string }[] = [
    {
      key: 'earned',
      label: t('affiliates.payouts.tiles.earned'),
      value: money(summary?.totalEarnings),
    },
    {
      key: 'paid',
      label: t('affiliates.payouts.tiles.paid'),
      value: money(summary?.paidEarnings),
    },
    {
      key: 'due',
      label: t('affiliates.payouts.tiles.due'),
      value: money(summary?.dueEarnings),
    },
    {
      key: 'unclaimed',
      label: t('affiliates.payouts.tiles.unclaimed'),
      value: money(summary?.unclaimedEarnings),
    },
    {
      key: 'lifetimeEarned',
      label: t('affiliates.payouts.tiles.lifetimeEarned'),
      value: money(summary?.lifetimeEarnings),
    },
    {
      key: 'lifetimePaid',
      label: t('affiliates.payouts.tiles.lifetimePaid'),
      value: money(summary?.lifetimePaidEarnings),
    },
  ];

  const parsedAmount = Number(amount);

  const validate = (): boolean => {
    const nextErrors: { email?: string; amount?: string } = {};
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      nextErrors.email = t('affiliates.payouts.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      nextErrors.email = t('affiliates.payouts.errors.emailInvalid');
    }
    // An empty string coerces to 0, so the blank case is rejected explicitly.
    if (amount.trim() === '' || !Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      nextErrors.amount = t('affiliates.payouts.errors.amountInvalid');
    } else if (due !== null && parsedAmount > due) {
      nextErrors.amount = t('affiliates.payouts.errors.amountExceedsDue', {
        amount: formatCurrency(due),
      });
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  /**
   * Dismissal is refused while the request is in flight: PayPal cannot recall a
   * batch, so a dialog that vanished mid-submit would read as a cancellation
   * that never happened and invite a second payout. Escape, the backdrop and the
   * header close all route through `onClose`, so one guard covers every path.
   */
  const guardWhilePending = (close: () => void) => (pending ? noop : close);

  const send = async () => {
    setSubmitError(null);
    setPending(true);
    try {
      const payout = await affiliateService.sendPayout({
        affiliateUserId: affiliate.affiliateUserId,
        influencerEmail: email.trim(),
        amount: parsedAmount,
        note: note.trim(),
      });
      ToastService.show({
        type: 'success',
        message: payout.batchStatus
          ? t('affiliates.payouts.toasts.submittedWithStatus', {
              status: payout.batchStatus,
            })
          : t('affiliates.payouts.toasts.submitted'),
      });
      // Closed rather than reset: PayPal settles asynchronously, so a re-read
      // still counts this batch as owed and leaving the form armed with the
      // amount just sent invites a duplicate payout. The list re-reads instead,
      // and reopening the dialog starts from a fresh balance.
      onPaid();
      onClose();
    } catch (cause) {
      setConfirming(false);
      setSubmitError(getUserFacingErrorMessage(cause, t('common.errorTitle')));
      setPending(false);
    }
  };

  // Replaces this dialog rather than stacking on top of it, matching the
  // referral-code delete flow and avoiding two competing focus traps. The `key`
  // forces a remount between the two steps: reusing the fiber would keep focus
  // on the very button that opened the confirmation — one node, same label —
  // so a second Enter would pay out before the operator read anything, and no
  // dialog change would be announced.
  if (confirming) {
    return (
      <Modal
        key={CONFIRM_STEP_KEY}
        open
        title={t('affiliates.payouts.confirm.title')}
        description={email.trim()}
        onClose={guardWhilePending(() => setConfirming(false))}
        footer={
          <>
            <Button
              variant="secondary"
              disabled={pending}
              onClick={() => setConfirming(false)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={pending} onClick={send}>
              {t('affiliates.payouts.confirm.send')}
            </Button>
          </>
        }>
        <p>
          {t('affiliates.payouts.confirm.description', {
            amount: formatCurrency(parsedAmount),
            email: email.trim(),
          })}
        </p>
        <p className="page__note">{t('affiliates.payouts.confirm.asyncNote')}</p>
      </Modal>
    );
  }

  return (
    <Modal
      key={FORM_STEP_KEY}
      open
      title={t('affiliates.payouts.title')}
      description={affiliate.email}
      onClose={guardWhilePending(onClose)}
      footer={
        <>
          <Button variant="secondary" disabled={pending} onClick={onClose}>
            {t('common.close')}
          </Button>
          <Button
            disabled={pending || !payable || !rangeUsable || status !== 'ready'}
            onClick={() => {
              if (validate()) {
                setConfirming(true);
              }
            }}>
            {t('affiliates.payouts.send')}
          </Button>
        </>
      }>
      <p className="ui-modal__note">{t('affiliates.payouts.description')}</p>
      <FormError variant="banner" message={submitError} />

      {!rangeUsable ? (
        <FormError variant="banner" message={rangeMessage} />
      ) : status === 'error' ? (
        <>
          <FormError variant="banner" message={error} />
          <div className="form-actions">
            <Button variant="secondary" onClick={reload}>
              {t('common.retry')}
            </Button>
          </div>
        </>
      ) : (
        <dl className="affiliate-earnings__tiles">
          {tiles.map(tile => (
            <div className="affiliate-earnings__tile" key={tile.key}>
              <dt className="affiliate-earnings__tile-label">{tile.label}</dt>
              <dd className="affiliate-earnings__tile-value">
                {status === 'loading' ? t('common.loading') : tile.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {rangeUsable ? (
        <p className="page__note">{t('affiliates.payouts.periodNote')}</p>
      ) : null}
      {unpayableNote ? <p className="page__note">{unpayableNote}</p> : null}

      <div className="form-grid">
        <TextField
          label={t('affiliates.payouts.emailLabel')}
          type="email"
          value={email}
          hint={t('affiliates.payouts.emailHint')}
          error={errors.email}
          onChange={event => setEmail(event.target.value)}
        />
        <TextField
          label={t('affiliates.payouts.amountLabel')}
          type="number"
          min={0}
          step="0.01"
          value={amount}
          error={errors.amount}
          onChange={event => {
            amountTouched.current = true;
            setAmount(event.target.value);
          }}
        />
        <TextField
          label={t('affiliates.payouts.noteLabel')}
          placeholder={t('affiliates.payouts.notePlaceholder')}
          value={note}
          onChange={event => setNote(event.target.value)}
        />
      </div>
    </Modal>
  );
}
